import { Badge, BadgeProps } from "@oysterhr/ui"

import {
  CustomerBankAccountMandateStateEnumType,
  B2BPaymentMethod,
  PaymentMethodState,
  CorePaymentMethod,
} from "@oysterhr/schema"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { BadgeStateItem } from "~/shared/components/StatesDescription"
import { useTranslation } from "react-i18next"
import { isDeactivated, isFtePaymentMethod } from "../../pages/CompanyPaymentMethodsPage/utils"
import { PaymentEngagementType } from "../../pages/CompanyPaymentMethodsPage/enums"

type PaymentMethodStatusBadgeProps = {
  paymentMethod: CorePaymentMethod | B2BPaymentMethod
  usedFor: PaymentEngagementType.Contractor | PaymentEngagementType.Fte
  size?: BadgeProps["size"]
}

export const NOT_ALLOWED_STATE: BadgeStateItem = {
  translationKey: "notAllowed.title",
  descriptionKey: "notAllowed.description",
  variation: "gray",
}

export const DEACTIVATED_STATE: BadgeStateItem = {
  translationKey: "deactivated.title",
  descriptionKey: "deactivated.description",
  variation: "error",
}

export const DISABLED_STATE: BadgeStateItem = {
  translationKey: "disabled.title",
  descriptionKey: "disabled.description",
  variation: "gray",
}

const VERIFTING_STATE: BadgeStateItem = {
  translationKey: "verifying.title",
  descriptionKey: "verifying.description",
  variation: "info",
}

type CorePaymentMethodState =
  | PaymentMethodState.InVerification
  | PaymentMethodState.Verified
  | PaymentMethodState.AuthorizedForDirectDebit
  | PaymentMethodState.VerificationFailed

export const CorePaymentMethodStateMap: Record<CorePaymentMethodState, BadgeStateItem> = {
  [PaymentMethodState.InVerification]: {
    translationKey: "inVerification.title",
    descriptionKey: "inVerification.description",
    variation: "gray",
  },
  [PaymentMethodState.Verified]: {
    translationKey: "verified.title",
    descriptionKey: "verified.description",
    variation: "info",
  },
  [PaymentMethodState.AuthorizedForDirectDebit]: {
    translationKey: "active.title",
    descriptionKey: "active.description",
    variation: "success",
  },
  [PaymentMethodState.VerificationFailed]: {
    translationKey: "verificationFailed.title",
    descriptionKey: "verificationFailed.description",
    variation: "error",
  },
}

const getCorePaymentMethodState = (
  paymentMethod: CorePaymentMethod,
  usedFor: PaymentMethodStatusBadgeProps["usedFor"],
) => {
  if (isDeactivated(paymentMethod)) return DEACTIVATED_STATE

  if (
    (usedFor === PaymentEngagementType.Contractor && paymentMethod.disabledForContractors) ||
    (usedFor === PaymentEngagementType.Fte && paymentMethod.disabledForFtes)
  )
    return DISABLED_STATE

  return CorePaymentMethodStateMap[paymentMethod.state as CorePaymentMethodState]
}

type B2BPaymentMethodState =
  | PaymentMethodState.Initialized
  | PaymentMethodState.AuthorizedByUser
  | PaymentMethodState.Verifying
  | PaymentMethodState.VerificationFailed
  | PaymentMethodState.AuthorizedForDirectDebit

export const B2BPaymentMethodStateMap: Record<
  Exclude<B2BPaymentMethodState, PaymentMethodState.AuthorizedByUser>,
  BadgeStateItem
> = {
  [PaymentMethodState.Initialized]: {
    translationKey: "initialized.title",
    descriptionKey: "initialized.description",
    variation: "warning",
  },
  [PaymentMethodState.Verifying]: VERIFTING_STATE,
  [PaymentMethodState.VerificationFailed]: {
    translationKey: "verificationFailed.title",
    descriptionKey: "verificationFailed.description",
    variation: "error",
  },
  [PaymentMethodState.AuthorizedForDirectDebit]: {
    translationKey: "active.title",
    descriptionKey: "active.description",
    variation: "success",
  },
}

export const B2BMandateStateMap: Record<CustomerBankAccountMandateStateEnumType, BadgeStateItem> = {
  [CustomerBankAccountMandateStateEnumType.Initialized]: {
    translationKey: "pendingSignature.title",
    descriptionKey: "pendingSignature.description",
    variation: "warning",
  },
  [CustomerBankAccountMandateStateEnumType.Signed]: {
    translationKey: "pendingSharing.title",
    descriptionKey: "pendingSharing.description",
    variation: "warning",
  },
  [CustomerBankAccountMandateStateEnumType.Lodged]: VERIFTING_STATE,
  [CustomerBankAccountMandateStateEnumType.Active]: {
    translationKey: "active.title",
    descriptionKey: "active.description",
    variation: "success",
  },
}

const getB2BPaymentMethodState = (customerBankAccount: B2BPaymentMethod): BadgeStateItem => {
  const state = customerBankAccount.state as B2BPaymentMethodState
  const mandateState = customerBankAccount?.mandate
    ?.state as CustomerBankAccountMandateStateEnumType

  if (customerBankAccount.disabledForFtes) return DISABLED_STATE

  if (state === PaymentMethodState.AuthorizedByUser) {
    if (customerBankAccount.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH)
      return VERIFTING_STATE

    return B2BMandateStateMap[mandateState]
  }

  return B2BPaymentMethodStateMap[state]
}

export const PaymentMethodStatusBadge = ({
  paymentMethod,
  usedFor,
  size = "md",
}: PaymentMethodStatusBadgeProps) => {
  const { t } = useTranslation(
    "company_interface/CompanyPaymentMethodsPage/PaymentMethodStatusBadge",
  )

  const state = isFtePaymentMethod(paymentMethod)
    ? getB2BPaymentMethodState(paymentMethod)
    : getCorePaymentMethodState(paymentMethod, usedFor)

  return (
    <Badge size={size} variation={state.variation}>
      {t(state.translationKey)}
    </Badge>
  )
}
